import { TypedDocumentNode, gql } from '@apollo/client';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';

import { hasGamesStarted, isFixtureOpened } from 'lib/so5';

import { useContenderStatus_so5LeaderboardContender } from './__generated__/useContenderStatus.graphql';

export enum ContenderStatus {
  EMPTY = 'EMPTY',
  NOT_STARTED = 'NOT_STARTED',
  DIVISION_CHANGE = 'DIVISION_CHANGE',
  TARGET = 'TARGET',
  THRESHOLD = 'THRESHOLD',
  INCOMPLETE = 'INCOMPLETE',
  FINISHED = 'FINISHED',
}

type ReturnType = {
  status: ContenderStatus;
  divisionChange?: DivisionChange;
};

export const useContenderStatus = (
  so5LeaderboardContender: Nullable<useContenderStatus_so5LeaderboardContender>
): ReturnType | null => {
  if (!so5LeaderboardContender) {
    return null;
  }

  const { managerTeam, so5Leaderboard, so5Lineup } = so5LeaderboardContender;
  const isUpcoming = isFixtureOpened(so5Leaderboard.so5Fixture);

  const projectedLineup = managerTeam?.divisionChangingSo5Lineup;
  const projectedRanking = projectedLineup?.so5Rankings[0];
  const ranking = so5Lineup?.so5Rankings[0];
  const divisionChange = isUpcoming
    ? projectedRanking?.eligibleDivisionChange
    : ranking?.eligibleDivisionChange;

  const hasLineupGamesStarted = so5Lineup ? hasGamesStarted(so5Lineup) : true;

  if (isUpcoming && so5Lineup && !so5Lineup.confirmable) {
    return { status: ContenderStatus.INCOMPLETE };
  }
  if (
    (so5Lineup && isUpcoming && !divisionChange) ||
    (!isUpcoming && !hasLineupGamesStarted)
  ) {
    return { status: ContenderStatus.NOT_STARTED };
  }
  if (ranking?.targetedRewardWithEligibility?.reward.target) {
    return { status: ContenderStatus.TARGET };
  }
  if (so5Leaderboard.rewardsConfig?.conditional?.length) {
    return { status: ContenderStatus.THRESHOLD };
  }
  if (divisionChange) {
    return { status: ContenderStatus.DIVISION_CHANGE, divisionChange };
  }
  if (!so5Lineup) {
    return { status: ContenderStatus.EMPTY };
  }

  return null;
};

useContenderStatus.fragments = {
  so5LeaderboardContender: gql`
    fragment useContenderStatus_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Leaderboard {
        slug
        rewardsConfig {
          conditional {
            score
          }
        }
        so5Fixture {
          slug
          ...isFixtureOpened_so5Fixture
        }
      }
      so5Lineup {
        id
        confirmable
        so5Rankings {
          id
          eligibleDivisionChange
          targetedRewardWithEligibility {
            reward {
              target {
                slug
              }
            }
          }
        }
        ...hasGamesStarted_so5Lineup
      }
      managerTeam {
        id
        divisionChangingSo5Lineup {
          id
          so5Rankings {
            id
            eligibleDivisionChange
          }
        }
      }
    }
    ${isFixtureOpened.fragments.so5Fixture}
    ${hasGamesStarted.fragments.so5Lineup}
  ` as TypedDocumentNode<useContenderStatus_so5LeaderboardContender>,
};
